import { normalCDF } from './math.js';

/**
 * Returns the HR zone BPM thresholds based on user preferences or sensible defaults.
 * @param {Object} [customThresholdsOrState] - Optional explicit state or hrZoneThresholds object
 * @returns {{z2Min: number, z3Min: number, z4Min: number, z5Min: number}}
 */
export function getHRZoneThresholds(customThresholdsOrState) {
  let hz = {};
  if (customThresholdsOrState) {
    hz = customThresholdsOrState.hrZoneThresholds || customThresholdsOrState;
  } else if (typeof state !== 'undefined' && state && state.hrZoneThresholds) {
    hz = state.hrZoneThresholds;
  } else if (typeof window !== 'undefined' && window.state && window.state.hrZoneThresholds) {
    hz = window.state.hrZoneThresholds;
  }

  return {
    z2Min: parseInt(hz.z2Min || 125, 10),
    z3Min: parseInt(hz.z3Min || 141, 10),
    z4Min: parseInt(hz.z4Min || 156, 10),
    z5Min: parseInt(hz.z5Min || 170, 10)
  };
}

/**
 * Calculates Karvonen Heart Rate Reserve (HRR) Zone boundaries (Zones 2-5).
 * Formula: Target HR = HR_rest + (Intensity % * HRR) where HRR = max(20, HR_max - HR_rest).
 * Zone 2: 60-70% HRR (z2Min = rest + 0.60 * HRR)
 * Zone 3: 70-80% HRR (z3Min = rest + 0.70 * HRR)
 * Zone 4: 80-90% HRR (z4Min = rest + 0.80 * HRR)
 * Zone 5: 90%+ HRR   (z5Min = rest + 0.90 * HRR)
 * 
 * @param {number|string} maxHr - Maximum heart rate in bpm
 * @param {number|string} restingHr - Resting heart rate in bpm
 * @returns {{z2Min: number, z3Min: number, z4Min: number, z5Min: number}}
 */
export function calculateKarvonenHRRThresholds(maxHr, restingHr) {
  const max = parseInt(maxHr, 10) || 190;
  const rest = parseInt(restingHr, 10) || 60;
  const hrr = Math.max(20, max - rest);
  return {
    z2Min: Math.round(rest + 0.60 * hrr),
    z3Min: Math.round(rest + 0.70 * hrr),
    z4Min: Math.round(rest + 0.80 * hrr),
    z5Min: Math.round(rest + 0.90 * hrr)
  };
}

/**
 * Calculates continuous or modeled seconds spent in heart rate Zones 1 through 5.
 * @param {Object} workout - The workout object
 * @param {Object} [thresholds] - Optional zone thresholds
 * @returns {number[]} Array of 5 numbers representing seconds in [Z1, Z2, Z3, Z4, Z5]
 */
export function calculateWorkoutZoneSeconds(workout, thresholds) {
  if (!workout) return [0, 0, 0, 0, 0];
  const th = thresholds || getHRZoneThresholds();

  // 1. Primary Source of Truth: Raw continuous second-by-second zone telemetry
  if (workout.zones && Array.isArray(workout.zones) && workout.zones.some(z => Number(z) > 0)) {
    return [
      Math.max(0, Math.round(Number(workout.zones[0]) || 0)),
      Math.max(0, Math.round(Number(workout.zones[1]) || 0)),
      Math.max(0, Math.round(Number(workout.zones[2]) || 0)),
      Math.max(0, Math.round(Number(workout.zones[3]) || 0)),
      Math.max(0, Math.round(Number(workout.zones[4]) || 0))
    ];
  }

  // 2. Intra-Stage Heart Rate Distribution Model across all Zones 1-5
  if (workout.stages && Array.isArray(workout.stages) && workout.stages.length > 0) {
    const zoneSec = [0, 0, 0, 0, 0];

    workout.stages.forEach(st => {
      const durSec = ((parseInt(st.durationMin, 10) || 0) * 60) + (parseInt(st.durationSec, 10) || 0);
      const hrMean = parseInt(st.avgHr, 10) || 0;
      if (durSec <= 0 || hrMean <= 0) return;

      const maxHr = parseInt(st.maxHr || workout.maxHeartrate, 10) || (hrMean + 15);
      const stdDev = Math.max(6, Math.min(14, (maxHr - hrMean) / 1.8));

      const pZ1 = normalCDF(th.z2Min, hrMean, stdDev);
      const pZ2 = Math.max(0, normalCDF(th.z3Min, hrMean, stdDev) - normalCDF(th.z2Min, hrMean, stdDev));
      const pZ3 = Math.max(0, normalCDF(th.z4Min, hrMean, stdDev) - normalCDF(th.z3Min, hrMean, stdDev));
      const pZ4 = Math.max(0, normalCDF(th.z5Min, hrMean, stdDev) - normalCDF(th.z4Min, hrMean, stdDev));
      const pZ5 = Math.max(0, 1.0 - normalCDF(th.z5Min, hrMean, stdDev));

      zoneSec[0] += durSec * pZ1;
      zoneSec[1] += durSec * pZ2;
      zoneSec[2] += durSec * pZ3;
      zoneSec[3] += durSec * pZ4;
      zoneSec[4] += durSec * pZ5;
    });

    return [
      Math.max(0, Math.round(zoneSec[0])),
      Math.max(0, Math.round(zoneSec[1])),
      Math.max(0, Math.round(zoneSec[2])),
      Math.max(0, Math.round(zoneSec[3])),
      Math.max(0, Math.round(zoneSec[4]))
    ];
  }

  // 3. Overall Session Distribution Model across all Zones 1-5
  if (workout.avgHeartrate && workout.duration) {
    const mean = Number(workout.avgHeartrate);
    const durMin = Number(workout.duration);
    if (!isNaN(mean) && !isNaN(durMin) && mean > 0 && durMin > 0) {
      const durationSec = durMin * 60;
      const maxHr = parseInt(workout.maxHeartrate, 10) || (mean + 20);
      const stdDev = Math.max(8, Math.min(16, (maxHr - mean) / 2.0));

      const pZ1 = normalCDF(th.z2Min, mean, stdDev);
      const pZ2 = Math.max(0, normalCDF(th.z3Min, mean, stdDev) - normalCDF(th.z2Min, mean, stdDev));
      const pZ3 = Math.max(0, normalCDF(th.z4Min, mean, stdDev) - normalCDF(th.z3Min, mean, stdDev));
      const pZ4 = Math.max(0, normalCDF(th.z5Min, mean, stdDev) - normalCDF(th.z4Min, mean, stdDev));
      const pZ5 = Math.max(0, 1.0 - normalCDF(th.z5Min, mean, stdDev));

      return [
        Math.max(0, Math.round(durationSec * pZ1)),
        Math.max(0, Math.round(durationSec * pZ2)),
        Math.max(0, Math.round(durationSec * pZ3)),
        Math.max(0, Math.round(durationSec * pZ4)),
        Math.max(0, Math.round(durationSec * pZ5))
      ];
    }
  }

  return [0, 0, 0, 0, 0];
}
